import { motion } from "framer-motion";
import { PresenceVisuals } from "@/components/mirror/MirrorPresenceConfig";
import AvatarCore from "@/features/avatar/core/AvatarCore";
import { useAvatar } from "@/hooks/useAvatar";
import type { ElevenLabsAlignment } from "@/lib/visemeMapper";
import type { MirrorAnimationState } from "@/hooks/useMirrorState";
import {
  MIRROR_AVATURN_CONFIG,
  MIRROR_METAPERSON_CONFIG,
} from "./mirrorAvatarConfig";

interface MirrorAvatarProps {
  visuals: PresenceVisuals;
  entered: boolean;
  analyserNode?: AnalyserNode | null;
  animationUrl?: string;
  onReady?: () => void;
  // Fase 4 lip sync (opt-in pass-through to AvatarCore).
  audioElement?: HTMLAudioElement | null;
  alignment?: ElevenLabsAlignment | null;
  // Group B state machine (opt-in pass-through to AvatarCore).
  mirrorState?: MirrorAnimationState;
  stateAnimations?: Record<MirrorAnimationState, string>;
  // Ajuste C (PR #382): overrides OPCIONAIS de câmera repassados pro AvatarCore.
  // Default undefined → cai no config per-provider (comportamento atual). Só o
  // render da /mirror (MirrorChat) passa valores; MirrorReady et al ficam iguais.
  cameraDistanceOverride?: number;
  lookAtYOverride?: number;
}

const MirrorAvatar = ({
  visuals,
  entered,
  analyserNode,
  animationUrl,
  onReady,
  audioElement,
  alignment,
  mirrorState,
  stateAnimations,
  cameraDistanceOverride,
  lookAtYOverride,
}: MirrorAvatarProps) => {
  const { avatarUrl, effectiveProvider, loading: avatarLoading } = useAvatar();

  // Catalog override: useAvatar() reads provider from user_profiles/user_avatars DB,
  // which reflects the USER's saved avatar — NOT the picker's preview. When the
  // picker shows a prerendered slot, only avatarUrl gets swapped; effectiveProvider
  // remains "avaturn" (because that's what's saved in DB). Pre-13/05/26 this worked
  // because all 6 catalog slots were Avaturn. After substituting the MetaPerson
  // catalog (male-1/2/3, female-1/2/3), the path no longer matches the DB-stored
  // provider. Detect MP catalog slots via path so AvatarCore picks the right pipeline.
  // TODO v1.1: refactor to per-slot provider metadata so this regex doesn't need
  // to enumerate the catalog filenames.
  const isMpCatalogPath = !!avatarUrl && /\/prerendered\/(male|female)-\d+\.glb(\?|$)/i.test(avatarUrl);
  const effectiveProviderFinal = isMpCatalogPath ? 'metaperson' : effectiveProvider;
  const isAvaturn = effectiveProviderFinal === "avaturn";
  const config = isAvaturn ? MIRROR_AVATURN_CONFIG : MIRROR_METAPERSON_CONFIG;

  if (isMpCatalogPath) {
    console.log('[avatar:DIAG] MirrorAvatar: catalog MP detected via path override', { avatarUrl });
  }
  const t = `${visuals.transitionDuration}s ease`;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97 }}
      animate={{ opacity: entered ? 1 : 0, scale: entered ? 1 : 0.97 }}
      transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
      className="absolute top-[32px] left-0 right-0 h-[650px] z-10 pointer-events-none"
    >
      {/* Presence halo — reacts to state */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[44%] rounded-full"
        style={{
          width: `${visuals.haloSize}px`,
          height: `${visuals.haloSize}px`,
          background: `radial-gradient(circle, hsl(${visuals.haloHue} 65% 48% / ${visuals.haloIntensity}), transparent 68%)`,
          animation:
            visuals.haloBreathDuration > 0
              ? `mirror-breathe ${visuals.haloBreathDuration}s ease-in-out infinite`
              : "none",
          transition: `width ${t}, height ${t}, background ${t}`,
        }}
      />

      {/* Inner depth shadow — grounds the avatar */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[40%] w-[420px] h-[520px] rounded-full"
        style={{
          background:
            "radial-gradient(ellipse 55% 55% at 50% 45%, hsl(210 50% 10% / 0.8), transparent 75%)",
          transition: `all ${t}`,
        }}
      />

      {/* Avatar source — AvatarCore driven by per-provider config */}
      <div
        className="relative z-10 h-full pointer-events-auto"
        style={{
          opacity: visuals.avatarOpacity,
          filter: visuals.avatarFilter,
          transform: `scale(${visuals.avatarScale})`,
          transformOrigin: "center 42%",
          transition: `filter ${t}, opacity ${t}, transform ${t}`,
        }}
      >
        {/* Idle presence animation — subtle breath + float */}
        <motion.div
          animate={{ scale: [1, 1.02, 1], y: [0, -3, 0] }}
          transition={{
            duration: 4.5,
            ease: "easeInOut",
            repeat: Infinity,
            repeatType: "loop",
          }}
          style={{
            width: "100%",
            height: "100%",
            transformOrigin: "center 42%",
            touchAction: "none",
          }}
        >
          {!avatarLoading && (
            <AvatarCore
              modelUrl={avatarUrl || "/avatar/model_small.glb"}
              height="100%"
              framing={config.framing}
              isAvaturnModel={isAvaturn}
              enableRotate={true}
              scaleOverride={config.scaleOverride}
              cameraDistanceOverride={cameraDistanceOverride ?? config.cameraDistanceOverride}
              lookAtYOverride={lookAtYOverride ?? config.lookAtYOverride}
              modelTiltXRadians={config.modelTiltXRadians}
              animationUrl={animationUrl ?? config.animationUrl}
              analyserNode={analyserNode}
              audioElement={audioElement}
              alignment={alignment}
              mirrorState={mirrorState}
              stateAnimations={stateAnimations}
              onReady={onReady}
            />
          )}
        </motion.div>
      </div>
    </motion.div>
  );
};

export default MirrorAvatar;
